import React, { useEffect, useMemo, useState } from "react";

import { LoadingIcon as Loading, Scrollbox } from "@packages/ds";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";

import PageMeta from "Components/PageMeta";
import Button from "ds/Button";
import Select from "ds/Select";
import useQueryParams from "Hooks/useQueryStrings";
import {
  setupCatalogLoadingSelector,
  setupCatalogSelector
} from "Reducers/setupCatalog/setupCatalog.selectors";
import { useSaveSetupOptionsConfig } from "Reducers/setupOptions/hooks/useSaveSetupOptionsConfig";
import { useAppSelector } from "Store/hooks";

import * as S from "./Template.styles";
import TemplateItem from "./TemplateItem";
import TemplateSelectModal from "./TemplateSelectModal";

const TemplateStep = ({ goToStep }) => {
  const intl = useIntl();

  const filterAll = useMemo(
    () => ({
      type: intl.formatMessage({ id: "template_filter_all_tag" }),
      language: intl.formatMessage({ id: "template_filter_all_language" })
    }),
    []
  );

  const {
    query: querySearch,
    lang: queryLanguage,
    type: queryType
  } = useQueryParams(["lang", "type", "query"]);

  const [language, setLanguage] = useState(null);
  const [modal, setModal] = useState({ isOpen: false, template: null });
  const [search, setSearch] = useState(querySearch || "");
  const [type, setType] = useState(null);

  const isLoadingCatalog = useAppSelector(setupCatalogLoadingSelector);

  const saveSetupOptionsConfig = useSaveSetupOptionsConfig();

  const updateUrlQueryString = () => {
    const url = new URL(window.location.href);
    language
      ? url.searchParams.set("lang", language)
      : url.searchParams.delete("lang");
    type ? url.searchParams.set("type", type) : url.searchParams.delete("type");
    search
      ? url.searchParams.set("query", search)
      : url.searchParams.delete("query");
    window.history.pushState({}, "", url);
  };

  useEffect(() => {
    updateUrlQueryString();
  }, [type, search, language]);

  const { data: templates } = useAppSelector(setupCatalogSelector);

  const { languageOptions, typeOptions } = useMemo(() => {
    let [languageOptions, typeOptions] = [[], []];
    if (templates) {
      const options = templates?.reduce(
        (accum, tpl) => {
          const tags = tpl?.info?.tags;
          if (tags) {
            accum?.language.add(tags[0]);
            tags.forEach(
              tag => !accum?.language.has(tag) && accum?.type.add(tag)
            );
          }
          return accum;
        },
        { language: new Set(), type: new Set() }
      );

      const sortFunc = (a, b) => a.toLowerCase().localeCompare(b.toLowerCase());

      languageOptions = Array.from(options?.language)
        .sort(sortFunc)
        .map(lang => ({ value: lang, label: lang }));
      languageOptions.unshift({
        value: filterAll.language,
        label: filterAll.language
      });

      typeOptions = Array.from(options?.type)
        .sort(sortFunc)
        .map(lang => ({ value: lang, label: lang }));
      typeOptions.unshift({ value: filterAll.type, label: filterAll.type });
    }
    return { languageOptions, typeOptions };
  }, [templates]);

  useEffect(() => {
    if (typeOptions || languageOptions) {
      const decodeQueryLang = queryLanguage || "";
      const decodeQueryType = queryType || "";
      if (
        typeOptions.some(
          t => t.value.toLowerCase() === decodeQueryType.toLowerCase()
        )
      )
        setType(decodeQueryType);
      if (
        languageOptions.some(
          t => t.value.toLowerCase() === decodeQueryLang.toLowerCase()
        )
      )
        setLanguage(decodeQueryLang);
    }
  }, [queryLanguage, queryType, typeOptions, languageOptions]);

  const isInList = tpl => {
    const searchTemplate = (searchTerm, includeOnEmptySearch = true) => {
      return searchTerm
        ? tpl.info?.name.toLowerCase().includes(searchTerm.toLowerCase())
        : includeOnEmptySearch;
    };
    const tags = tpl.info?.tags || [];

    if (language === filterAll.language) {
      if (type === filterAll.type || type === null)
        return searchTemplate(search);
      if (tags.includes(type)) {
        return searchTemplate(search);
      }
    }
    if (type && type !== filterAll.type && !tags.includes(type)) return false;
    if (language && !tags.includes(language)) return false;
    if (search && !tpl.info?.name.toLowerCase().includes(search.toLowerCase()))
      return false;
    return true;
  };

  const filteredList = [...templates]
    ?.sort((a, b) =>
      a.info.name.toLowerCase().localeCompare(b.info.name.toLowerCase())
    )
    .filter(isInList);

  const handleSelectTemplate = obj => {
    saveSetupOptionsConfig({
      template: obj.template
    });
    goToStep("info");
  };

  const handleGoBack = e => {
    e?.preventDefault();
    goToStep("");
  };

  return (
    <S.Wrapper
      id="template-list"
      role="tabpanel"
      aria-labelledby="tab-template"
    >
      <PageMeta title={intl.formatMessage({ id: `setup.template.title` })} />
      <S.Header>
        <S.Title tag="h2">
          {intl.formatMessage({ id: `setup.template.title` })}
        </S.Title>
      </S.Header>

      <S.FiltersWrapper>
        <S.SearchInput
          placeholder={intl.formatMessage({ id: "search_templates" })}
          onChange={value => setSearch(value)}
          value={search}
          closedByDefault
          onBlurState
        />

        <S.SelectorsWrapper>
          <S.SelectorWrapper className="filter-tags filter-type">
            <Select
              options={typeOptions}
              name="select_type"
              id="select_type"
              onChange={elt => {
                setType(typeof elt.value !== "string" ? null : elt.value);
              }}
              value={type ? { value: type, label: type } : undefined}
              defaultValue={{ value: filterAll.type, label: filterAll.type }}
              isSearchable={false}
              isClearable={false}
              isMulti={false}
              className="outlined"
              styles={{
                control: base => ({
                  ...base,
                  borderWidth: "0px !important",
                  backgroundColor: "inherit"
                }),
                indicatorSeparator: base => ({ ...base, display: "none" })
              }}
            />
          </S.SelectorWrapper>
          <S.SelectorWrapper className="filter-tags filter-language">
            <Select
              options={languageOptions}
              name="select_language"
              id="select_language"
              onChange={elt => {
                setLanguage(typeof elt.value !== "string" ? null : elt.value);
              }}
              value={
                language ? { value: language, label: language } : undefined
              }
              defaultValue={{
                value: filterAll.language,
                label: filterAll.language
              }}
              isSearchable={false}
              isClearable={false}
              isMulti={false}
              className="outlined"
              styles={{
                control: base => ({
                  ...base,
                  borderWidth: "0px !important",
                  backgroundColor: "inherit"
                }),
                indicatorSeparator: base => ({ ...base, display: "none" })
              }}
            />
          </S.SelectorWrapper>
        </S.SelectorsWrapper>
      </S.FiltersWrapper>

      <Scrollbox>
        <S.ResultsWrapper className="templates-wrapper">
          {filteredList?.map((tpl, i) => {
            return (
              <TemplateItem
                key={`template-${i}`}
                template={tpl}
                onClick={() => handleSelectTemplate(tpl)}
                openModal={() => {
                  setModal({ isOpen: true, template: tpl });
                }}
                modalOpen={modal.isOpen}
              />
            );
          })}
        </S.ResultsWrapper>
        {isLoadingCatalog && (
          <S.LoadingWrapper>
            <Loading />
          </S.LoadingWrapper>
        )}
      </Scrollbox>

      <S.ActionButtons>
        <Button variant="secondary" onClick={handleGoBack} analyticId="back">
          {intl.formatMessage({ id: "back" })}
        </Button>
      </S.ActionButtons>

      {modal.isOpen && (
        <TemplateSelectModal
          isOpen={modal.isOpen}
          template={modal.template}
          onSave={() => handleSelectTemplate(modal.template)}
          closeModal={() => setModal({ isOpen: false, template: null })}
        />
      )}
    </S.Wrapper>
  );
};

TemplateStep.propTypes = {
  goToStep: PropTypes.func,
  organizationNameId: PropTypes.string
};

export default TemplateStep;
